/**
 * Shared TypeScript types for the frontend application
 */

export interface Dealership {
  locationId: number;
  name: string;
}

export type Department =
  | 'NEW_VEHICLE'
  | 'USED_VEHICLE'
  | 'SERVICE'
  | 'PARTS'
  | 'BODY_SHOP'
  | 'RENTAL'
  | 'RECON'
  | 'OTHER'
  | 'MISC'
  | 'UNKNOWN';

/**
 * Expense categories matching DealerBuilt DOC report structure
 */
export type ExpenseCategory =
  | 'VARIABLE'       // Variable expenses (New/Used)
  | 'SEMI_FIXED'     // Semi-fixed expenses (New/Used)
  | 'SALES'          // Sales expenses (Service/Parts/Body Shop)
  | 'FIXED'          // Fixed expenses (all departments)
  | 'OTHERS'         // Owner compensation
  | 'RENTAL_INCOME'  // Rental income
  | 'RENTAL_EXPENSE' // Rental expenses
  | 'OTHER_INCOME'   // Other income (Others dept)
  | 'CLEARING'       // Clearing accounts (Misc dept)
  | 'GROSS'          // Revenue/Gross accounts
  | 'UNKNOWN';       // Unmapped

// Legacy type for backward compatibility
export type MetricCategory =
  | 'SALES'
  | 'COST_OF_SALE'
  | 'FI_INCOME'
  | 'SALES_EXPENSE'
  | 'FIXED_EXPENSE'
  | 'OTHER_EXPENSE'
  | 'OTHER_INCOME'
  | 'UNKNOWN';

/**
 * Monthly totals for seasonality calculations
 */
export interface MonthlyTotals {
  month1: number;   // January
  month2: number;   // February
  month3: number;   // March
  month4: number;   // April
  month5: number;   // May
  month6: number;   // June
  month7: number;   // July
  month8: number;   // August
  month9: number;   // September
  month10: number;  // October
  month11: number;  // November
  month12: number;  // December
}

/**
 * Monthly breakdown for all department metrics
 */
export interface DepartmentMonthlyData {
  sales: MonthlyTotals;
  costOfSale: MonthlyTotals;
  frontGross: MonthlyTotals;
  fiIncome: MonthlyTotals;
  totalGross: MonthlyTotals;
  // Expense categories (matching DOC report)
  variableExpenses: MonthlyTotals;   // Variable Expenses (New/Used)
  semiFixedExpenses: MonthlyTotals;  // Semi-Fixed Expenses (New/Used)
  salesExpenses: MonthlyTotals;      // Sales Expenses (Service/Parts/Body Shop)
  fixedExpenses: MonthlyTotals;
  otherExpenses: MonthlyTotals;
  totalExpenses: MonthlyTotals;
  // Calculated metrics
  variableGross: MonthlyTotals;      // Total Gross - Variable Expenses
  sellingGross: MonthlyTotals;       // Variable Gross - Semi-Fixed Expenses
  operatingProfit: MonthlyTotals;
  otherIncome: MonthlyTotals;
}

export interface AccountData {
  account: string;
  description?: string;
  ytdTotal: number;
  transactionCount: number;
  loadedAt: string;
  department?: Department;
  departmentName?: string;
  category?: MetricCategory;
  expenseCategory?: ExpenseCategory;  // New DOC-based category
  docClass?: string;                  // DOC class (Gross, Expense, Net Adds)
  monthlyTotals?: MonthlyTotals;
}

export interface DepartmentSummary {
  department: Department;
  departmentName: string;
  // Core metrics (matching DOC report)
  sales: number;
  costOfSale: number;
  frontGross: number;           // Front End Gross (Gross in DOC)
  fiIncome: number;             // F&I Income (Back End Gross)
  totalGross: number;           // Total Gross = frontGross + fiIncome
  // Expense breakdown by category (matching DOC report)
  variableExpenses: number;     // Variable Expenses (New/Used only)
  semiFixedExpenses: number;    // Semi-Fixed Expenses (New/Used only)
  salesExpenses: number;        // Sales Expenses (Service/Parts/Body Shop)
  fixedExpenses: number;        // Fixed Expenses (all departments)
  otherExpenses: number;        // Others (owner compensation)
  totalExpenses: number;        // Total Expenses
  // Calculated metrics
  variableGross: number;        // Total Gross - Variable Expenses
  sellingGross: number;         // Variable Gross - Semi-Fixed Expenses
  operatingProfit: number;      // Total Gross - Total Expenses
  otherIncome: number;          // For Others department
  accountCount: number;
  monthlyData?: DepartmentMonthlyData;
}

export interface LocationSummary {
  totalSales: number;
  totalCostOfSale: number;
  totalFrontGross: number;
  totalFiIncome: number;
  totalOtherIncome: number;
  totalGross: number;
  totalExpenses: number;
  operatingProfit: number;
  departmentCount: number;
  accountCount: number;
  monthlyData?: {
    totalSales: MonthlyTotals;
    totalGross: MonthlyTotals;
    totalExpenses: MonthlyTotals;
    operatingProfit: MonthlyTotals;
  };
}

/**
 * Monthly unit counts by brand
 * Key is brand name, value is monthly breakdown
 */
export interface BrandMonthlyUnits {
  [brand: string]: MonthlyTotals;
}

export interface UnitCounts {
  newVehicleUnits: Record<string, number>;
  usedVehicleUnits: Record<string, number>;
  totalNewUnits: number;
  totalUsedUnits: number;
  monthlyNewUnits?: MonthlyTotals;
  monthlyUsedUnits?: MonthlyTotals;
  /** Monthly breakdown by brand for new vehicles */
  monthlyNewUnitsByBrand?: BrandMonthlyUnits;
  /** Monthly breakdown by brand for used vehicles */
  monthlyUsedUnitsByBrand?: BrandMonthlyUnits;
}

export interface HistoricalData {
  locationId: number;
  locationName: string;
  year: number;
  month?: number;
  reportType: string;
  dataSource?: 'DOC' | 'LEGACY' | 'HYBRID';
  departmentSummaries: DepartmentSummary[];
  locationSummary: LocationSummary;
  accounts: AccountData[];
  unitCounts?: UnitCounts;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  /** Set by endpoints that use optimistic concurrency (e.g. Nut Sheet) when a
   *  write was rejected because the row changed since the client last read it.
   *  `data` then carries the current server copy. */
  conflict?: boolean;
}

/**
 * Month names for display
 */
export const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Helper to get monthly value by index (1-12)
 */
export function getMonthValue(totals: MonthlyTotals | undefined, month: number): number {
  if (!totals) return 0;
  const key = `month${month}` as keyof MonthlyTotals;
  return totals[key] || 0;
}

/**
 * Helper to create empty monthly totals
 */
export function createEmptyMonthlyTotals(): MonthlyTotals {
  return {
    month1: 0, month2: 0, month3: 0, month4: 0,
    month5: 0, month6: 0, month7: 0, month8: 0,
    month9: 0, month10: 0, month11: 0, month12: 0
  };
}

/**
 * Helper to add two MonthlyTotals together
 */
export function addMonthlyTotals(
  a: MonthlyTotals | undefined,
  b: MonthlyTotals | undefined
): MonthlyTotals {
  const aVal = a || createEmptyMonthlyTotals();
  const bVal = b || createEmptyMonthlyTotals();
  return {
    month1: aVal.month1 + bVal.month1,
    month2: aVal.month2 + bVal.month2,
    month3: aVal.month3 + bVal.month3,
    month4: aVal.month4 + bVal.month4,
    month5: aVal.month5 + bVal.month5,
    month6: aVal.month6 + bVal.month6,
    month7: aVal.month7 + bVal.month7,
    month8: aVal.month8 + bVal.month8,
    month9: aVal.month9 + bVal.month9,
    month10: aVal.month10 + bVal.month10,
    month11: aVal.month11 + bVal.month11,
    month12: aVal.month12 + bVal.month12,
  };
}
